import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, MousePointerClick, BadgePercent, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../../lib/api";
import { PageHeader, StatCard, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";

export default function CreatorAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/vid/creator/analytics").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return <LoadingRows rows={4} />;
  const t = data.totals;

  return (
    <div className="space-y-8" data-testid="creator-analytics-page">
      <PageHeader title="Analytics" subtitle="Real performance data — views, clicks, conversions" testid="analytics-title" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Eye} label="Total Views" value={t.views} testid="an-views" tint="bg-blue-500/10 text-blue-500" />
        <StatCard icon={MousePointerClick} label="Affiliate Clicks" value={t.clicks} testid="an-clicks" delay={0.05} tint="bg-amber-500/10 text-amber-500" />
        <StatCard icon={BadgePercent} label="Conversions" value={t.conversions} testid="an-convs" delay={0.1} tint="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={MousePointerClick} label="Conversion Rate" value={`${t.conversion_rate}%`} testid="an-rate" delay={0.15} tint="bg-violet-500/10 text-violet-500" />
        <StatCard icon={IndianRupee} label="Earnings" value={fmtINR(t.earnings)} testid="an-earnings" delay={0.2} />
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Top Videos</h2>
        {data.top_videos.length === 0 ? <EmptyState title="No videos yet" body="Add your first video to start tracking." /> : (
          <TableShell head={["Video", "Views", "Likes", "Clicks", "Conversions", "Commission"]} testid="an-top-videos">
            {data.top_videos.map((v) => (
              <TR key={v.id} testid={`an-video-${v.id}`}>
                <TD><Link to={`/v/${v.slug}`} className="font-semibold text-sm hover:text-primary transition-colors max-w-[260px] truncate block">{v.title}</Link></TD>
                <TD><span className="font-bold">{v.views}</span></TD>
                <TD>{v.likes}</TD>
                <TD>{v.clicks}</TD>
                <TD><span className="font-bold">{v.conversions}</span></TD>
                <TD><span className="font-bold text-emerald-600">{fmtINR(v.commission)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Top Affiliate Offers</h2>
        {data.top_offers.length === 0 ? <p className="text-sm text-muted-foreground">No conversions yet — attach offers to your videos.</p> : (
          <TableShell head={["Offer", "Conversions", "Commission Earned"]} testid="an-top-offers">
            {data.top_offers.map((o) => (
              <TR key={o.name} testid={`an-offer-${o.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <TD><span className="font-semibold text-sm">{o.name}</span></TD>
                <TD><span className="font-bold">{o.conversions}</span></TD>
                <TD><span className="font-bold text-emerald-600">{fmtINR(o.commission)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>
    </div>
  );
}
